import React, { createContext, FC, RefObject, useContext, useRef } from 'react'

type FocusProviderContextType = {
  focusElementWithKey: (key: string) => void
  getRefForKey: (key: string) => (ref: HTMLElement | null) => void
}

const FocusContext = createContext<FocusProviderContextType | undefined>(undefined)

interface FocusProviderProps {
  children: React.ReactNode
}

export const FocusProvider: FC<FocusProviderProps> = ({ children }) => {
  const refs = useRef<Map<string, RefObject<HTMLElement>>>(new Map())

  const focusElementWithKey = (key: string) => {
    const ref = refs.current.get(key)

    if (!ref || !ref.current) {
      return
    }
    ref.current.focus()
  }

  const getRefForKey = (key: string) => {
    return (ref: HTMLElement | null) => {
      if (!ref) {
        return
      }
      refs.current.set(key, { current: ref })
    }
  }

  return (
    <FocusContext.Provider
      value={{
        focusElementWithKey,
        getRefForKey,
      }}
    >
      {children}
    </FocusContext.Provider>
  )
}

export function useFocusContext() {
  const context = useContext<FocusProviderContextType | undefined>(FocusContext)
  if (context === undefined) {
    throw new Error('useFocusContext must be used within a FocusProvider')
  }

  return context
}
